import { ButtonGroup, Stack } from "@mui/material"
import { useEffect, useMemo, useRef, useState } from "react"
import { clone } from "lib/clone"
import { Bound } from "lib/@components/binding/Bound"
import { generate } from "library/guid/index.mjs"
import { CompareButtonsBelow } from "slot-definitions"
import { InlineAcceptButton } from "./inline-accept-button"
import { useSkills } from "routes/@lib/skills"
import { AcceptAll, IsAccepted, SendReviewUpdate } from "event-definitions"
import { useCurrentUser } from "lib/store/use-current-user"
import { InlineRejectButton } from "./inline-reject-button"
import { getInfoFor } from "../accept-button"
import { PAYLOAD_STATUS_APPROVED, PAYLOAD_STATUS_REJECTED } from "routes/audit/lib/audit-constants.mjs"
import { findTaskComparison } from "../lib/find-task-comparison"
import { NotComparisonReadOnlyInlineButtons } from "./not-comparison-read-only-inline-buttons"
import { useBoundContext } from "lib/@components/binding/use-bound-context"

export function InlineCompareButtonsBase({
    idFromGroup,
    to,
    from,
    rendered,
    schedule,
    canSave,
    popupEditor,
    groupReviewId,
    referenceId,
    taskId,
    audit,
    latestTaskAudit,
}) {
    const { hasAcceptReject } = useBoundContext()
    const [buttonId] = useState(() => idFromGroup ?? generate())
    const [localTask] = useState(clone(to))
    const auditIdRef = useRef(null)
    const [skills] = useSkills()
    const [original, tailored] = findTaskComparison(from, localTask, skills, buttonId)
    const taskIsHeader = Boolean(to?.isHeader ?? from?.isHeader)

    // The button owns its decision optimistically. It hydrates from the loaded decision (the
    // page gates rendering until decisions are loaded, so `audit` is present at mount); a click
    // sets an optimistic decision that overrides the server value until the write lands (revert
    // = clear it, falling back to the unchanged server value). No effect re-syncs from `audit` —
    // that continuous re-sync caused the old flash/stuck bugs. The banner reads `decisionPayload`
    // too, so the red rejection box is optimistic in step with the button.
    const [optimisticDecision, setOptimisticDecision] = useState(null)
    const decisionPayload = optimisticDecision ?? audit?.payload ?? null
    const status = decisionPayload?.status ?? undefined
    const accepted = deriveAccepted(status)

    const auditPayload = {
        original,
        tailored,
        isHeader: taskIsHeader,
        taskTitle: buttonId,
        taskId,
        status,
        groupReviewId: groupReviewId ?? schedule?.groupReviewId,
        subHeader: latestTaskAudit?.payload?.subHeader,
    }
    const user = useCurrentUser()

    useEffect(() => {
        IsAccepted.call({ isAccepted: accepted, id: buttonId })
    }, [accepted, buttonId])

    const uniqueId = useMemo(() => generate(), [])

    const info = getInfoFor(rendered)
    AcceptAll.useEvent(
        (accept, acceptedStatuses, someAccepted, allAccepted) => {
            sendForAcceptAll(accept, acceptedStatuses, someAccepted, allAccepted, auditPayload)
        },
        [auditPayload, groupReviewId]
    )

    return (
        <Bound
            buttonId={buttonId}
            idFromGroup={idFromGroup}
            referenceId={referenceId}
            schedule={schedule}
            status={status}
            taskId={taskId}
            auditPayload={auditPayload}
            userName={user.displayName}
            accepted={accepted}
            setOptimisticDecision={setOptimisticDecision}
            uniqueId={uniqueId}
            auditId={audit?.id ?? null}
            decisionPayload={decisionPayload}
            ensureAuditId={ensureAuditId}
            groupReviewId={groupReviewId}
        >
            {(canSave || hasAcceptReject) && !popupEditor && (
                <NotComparisonReadOnlyInlineButtons>
                    <Stack spacing={2} pb={2}>
                        <ButtonGroup>
                            <Stack direction="row" spacing={1}>
                                <InlineAcceptButton idFromGroup={idFromGroup} rendered={rendered} />
                                <InlineRejectButton idFromGroup={idFromGroup} />
                            </Stack>
                        </ButtonGroup>
                        <CompareButtonsBelow.Slot idFromGroup={idFromGroup} />
                        {info}
                    </Stack>
                </NotComparisonReadOnlyInlineButtons>
            )}
        </Bound>
    )

    function deriveAccepted(value) {
        return value === "approved" ? true : value === "rejected" ? false : undefined
    }

    function sendForAcceptAll(accept, acceptedStatuses, someAccepted, allAccepted, payload) {
        if (accept) {
            payload.shortDescription = "Change Accepted"
            payload.status = PAYLOAD_STATUS_APPROVED
            payload.event = "tailoring!custom-schedule-accept"
            payload.actionedOn = new Date().toISOString()
            setOptimisticDecision({
                status: PAYLOAD_STATUS_APPROVED,
                reason: "",
                userName: user.displayName,
                actionedOn: payload.actionedOn,
            })
            IsAccepted.call({ isAccepted: true, id: buttonId })
            SendReviewUpdate.call({ payload: { referenceId, payload }, auditId: ensureAuditId() })
        } else if (accept === false) {
            const changeAccepted = acceptedStatuses[buttonId] === true
            if (allAccepted || !someAccepted || (someAccepted && !changeAccepted)) {
                payload.shortDescription = "Change Rejected"
                payload.status = PAYLOAD_STATUS_REJECTED
                payload.event = "tailoring!custom-schedule-reject"
                payload.actionedOn = new Date().toISOString()
                setOptimisticDecision({
                    status: PAYLOAD_STATUS_REJECTED,
                    reason: payload.reason ?? "",
                    userName: user.displayName,
                    actionedOn: payload.actionedOn,
                })
                IsAccepted.call({ isAccepted: false, id: buttonId })
                SendReviewUpdate.call({ payload: { referenceId, payload }, auditId: ensureAuditId() })
            }
        }
    }

    function ensureAuditId() {
        if (audit?.id) return audit.id
        if (auditIdRef.current) return auditIdRef.current
        const newId = generate()
        auditIdRef.current = newId
        return newId
    }
}
